/**
 * Мастер местоположения аптеки (PROJECT_NOTES §37): замер устройства, уточнение пином на карте, подтверждение.
 * Работает одинаково в профиле, при первом входе и на телефоне по ссылке - отличается только backend.
 * backend должен быть стабильным между рендерами (константа или useMemo): от него зависит подбор адреса.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, InputAdornment, List, ListItemButton, ListItemText, Step, StepLabel, Stepper, TextField, Typography } from '@mui/material';
import { LocationOnOutlined, Search } from '@mui/icons-material';
import { notify } from '../notifications';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { GeoBackend, GeoPlace, SavedGeo, SavedPoint } from './geoApi';
import {
  distanceMeters, FAR_SHIFT_M, formatCoords, formatDistance, MIN_PIN_ZOOM, MIN_SEARCH, pointToSave, savedAccuracy, SHARP_PIN_ZOOM, startView
} from './geoMath';
import { useGeoMeasure } from './useGeoMeasure';
import MeasureStep from './MeasureStep';
import PinMap, { MapCommand, MapView } from './PinMap';
import { alertSx, BORDER, cardSx, hintSx, monoSx, MUTED, outlinedButtonSx, primaryButtonSx, PRIMARY, TEXT } from './geoStyles';

interface GeoWizardProps {
  backend: GeoBackend;
  /** Сохраненная точка: с нее начинается карта, если замер не удался */
  initial: SavedGeo | null;
  /** Открыто на телефоне */
  compact?: boolean;
  onSaved: (geo: SavedPoint, message: string) => void;
  /** Панель «С телефона» вместо шагов; back - вернуться к мастеру */
  renderPhone?: (back: () => void) => React.ReactNode;
}

type WizardStep = 'measure' | 'adjust' | 'confirm';
const STEPS: WizardStep[] = ['measure', 'adjust', 'confirm'];
const STEP_LABELS = ['Определение', 'Уточнение', 'Подтверждение'];

// Мельче адрес под пином - уже район, а не дом
const ADDRESS_ZOOM = 15;
const ADDRESS_DEBOUNCE_MS = 600;
const SEARCH_ZOOM = 18;

const sameView = (a: MapView | null, b: MapView) => Boolean(a && a.latitude === b.latitude && a.longitude === b.longitude && a.zoom === b.zoom);

const GeoWizard: React.FC<GeoWizardProps> = ({ backend, initial, compact = false, onSaved, renderPhone }) => {
  const [step, setStep] = useState<WizardStep>('measure');
  const [phone, setPhone] = useState(false);
  const { state: measure, start: startMeasure, stop: stopMeasure } = useGeoMeasure(true);

  const [mapStart, setMapStart] = useState<MapView | null>(null);
  const [view, setView] = useState<MapView | null>(null);
  const [command, setCommand] = useState<MapCommand | null>(null);
  const commandSeq = useRef(0);

  const [place, setPlace] = useState<GeoPlace | null>(null);
  const [placeLoading, setPlaceLoading] = useState(false);
  const lookupSeq = useRef(0);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeoPlace[] | null>(null);
  const [searching, setSearching] = useState(false);
  const searchSeq = useRef(0);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Адрес под пином: после остановки карты, старый ответ не перетирает новый
  useEffect(() => {
    if (!view) return undefined;
    const seq = ++lookupSeq.current;
    setPlace(null);
    if (view.zoom < ADDRESS_ZOOM) {
      setPlaceLoading(false);
      return undefined;
    }
    setPlaceLoading(true);
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      backend.address(view, controller.signal)
        .then(found => { if (seq === lookupSeq.current) setPlace(found); })
        .catch(() => { /* адрес необязателен: точка сохранится и без него */ })
        .finally(() => { if (seq === lookupSeq.current) setPlaceLoading(false); });
    }, ADDRESS_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [backend, view]);

  const handleMove = (next: MapView) => setView(prev => (sameView(prev, next) ? prev : next));

  const goToMap = () => {
    stopMeasure();
    setMapStart(startView(measure.best, initial));
    setStep('adjust');
  };

  const openPhone = () => {
    stopMeasure();
    setPhone(true);
  };

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < MIN_SEARCH) {
      notify.info(`Введите адрес: от ${MIN_SEARCH} символов`);
      return;
    }
    const seq = ++searchSeq.current;
    setSearching(true);
    try {
      const found = await backend.search(q, view);
      if (seq === searchSeq.current) setResults(found);
    } catch (err) {
      if (seq === searchSeq.current) notify.error(apiErrorMessage(err, 'Не удалось найти адрес'));
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  };

  const pickResult = (found: GeoPlace) => {
    commandSeq.current += 1;
    setCommand({ latitude: found.latitude, longitude: found.longitude, zoom: SEARCH_ZOOM, seq: commandSeq.current });
    setResults(null);
  };

  const point = view ? pointToSave(view, view.zoom, measure.best) : null;
  const best = measure.best;
  const shift = view && best ? distanceMeters(view, best) : null;
  const farShift = shift !== null && best !== null && shift > Math.max(FAR_SHIFT_M, best.accuracy);

  const save = async () => {
    if (!point) return;
    setSaving(true);
    setSaveError('');
    try {
      const { message, geolocation } = await backend.save(point);
      onSaved(geolocation, message);
    } catch (err) {
      const message = apiErrorMessage(err, 'Не удалось сохранить местоположение');
      setSaveError(message);
      notify.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (phone && renderPhone) return <>{renderPhone(() => setPhone(false))}</>;

  const pinLabel = !view || view.zoom < MIN_PIN_ZOOM ? 'Приблизьте карту' : placeLoading ? 'Определяем адрес...' : place ? (place.short || place.address) : '';
  const addressText = place
    ? place.address
    : placeLoading ? 'Определяем адрес...'
      : view && view.zoom < ADDRESS_ZOOM ? 'Приблизьте карту, чтобы увидеть адрес' : 'Адрес не определен';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Stepper
        activeStep={STEPS.indexOf(step)}
        alternativeLabel
        sx={{
          '& .MuiStepLabel-label': { fontSize: 12.5, fontWeight: 600, mt: '6px !important' },
          '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: PRIMARY }
        }}
      >
        {STEP_LABELS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      {step === 'measure' && (
        <MeasureStep measure={measure} compact={compact} canPhone={Boolean(renderPhone)} onRetry={startMeasure} onMap={goToMap} onPhone={openPhone} />
      )}

      {step === 'adjust' && mapStart && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={query}
              placeholder="Улица и дом, например Тауке хана 12"
              onChange={event => setQuery(event.target.value)}
              onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); runSearch(); } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: MUTED }} /></InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff', height: 40 } }}
            />
            <Button variant="outlined" onClick={runSearch} disabled={searching} sx={{ ...outlinedButtonSx, minWidth: 88 }}>
              {searching ? <CircularProgress size={18} /> : 'Найти'}
            </Button>
          </Box>

          {results && (results.length === 0 ? (
            <Typography sx={hintSx}>Ничего не найдено: уточните запрос или найдите здание на карте</Typography>
          ) : (
            <List dense disablePadding sx={{ border: `1px solid ${BORDER}`, borderRadius: '8px', maxHeight: 200, overflow: 'auto', bgcolor: '#fff' }}>
              {results.map(found => (
                <ListItemButton key={`${found.latitude},${found.longitude},${found.address}`} onClick={() => pickResult(found)}>
                  <ListItemText
                    primary={found.short || found.name || found.address}
                    secondary={found.address}
                    primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600, color: TEXT }}
                    secondaryTypographyProps={{ fontSize: 12, noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          ))}

          <Typography sx={hintSx}>Двигайте карту - пин остается по центру. Поставьте его на вход в аптеку и приблизьте, чтобы точность была выше.</Typography>

          <PinMap start={mapStart} command={command} measured={best} label={pinLabel} height={compact ? '52vh' : { xs: '50vh', sm: 360 }} onMove={handleMove} />

          <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <LocationOnOutlined sx={{ fontSize: 20, color: PRIMARY, mt: 0.1, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, color: place ? TEXT : MUTED, fontSize: '0.9rem', wordBreak: 'break-word' }}>{addressText}</Typography>
                {view && <Typography sx={{ ...monoSx, color: MUTED, fontSize: 12.5 }}>{formatCoords(view)}</Typography>}
              </Box>
            </Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: point ? '#15803d' : '#b45309' }}>
              {point
                ? `Точность точки ±${savedAccuracy(point)} м${point.source === 'gps' ? ' по замеру устройства' : ''}`
                : 'Приблизьте карту, чтобы поставить точку'}
            </Typography>
            {point && point.source === 'manual' && view && view.zoom < SHARP_PIN_ZOOM && (
              <Typography sx={hintSx}>Приблизьте еще, чтобы видеть вход в здание: точность станет выше</Typography>
            )}
            {point && point.source === 'manual' && shift !== null && (
              <Typography sx={hintSx}>Сдвиг от замера устройства: {formatDistance(shift)}</Typography>
            )}
          </Box>

          {farShift && shift !== null && (
            <Alert severity="warning" sx={alertSx}>Пин в {formatDistance(shift)} от замера устройства. Проверьте, что выбрано здание аптеки.</Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => setStep('measure')} sx={outlinedButtonSx}>Назад</Button>
            <Button variant="contained" disabled={!point} onClick={() => setStep('confirm')} sx={primaryButtonSx}>Далее</Button>
          </Box>
        </Box>
      )}

      {step === 'confirm' && point && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {[
              { label: 'Адрес', value: place ? place.address : placeLoading ? 'определяется...' : 'не определен: сервер попробует еще раз при сохранении' },
              { label: 'Координаты', value: formatCoords(point), mono: true },
              { label: 'Способ', value: point.source === 'gps' ? 'замер устройства' : point.measured ? 'пин на карте' : 'пин на карте, без замера устройства' },
              { label: 'Точность', value: `±${savedAccuracy(point)} м` }
            ].map(row => (
              <Box key={row.label} sx={{ display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <Typography sx={{ color: MUTED, fontSize: 13, minWidth: 96 }}>{row.label}</Typography>
                <Typography sx={row.mono ? monoSx : { color: TEXT, fontSize: '0.9rem', fontWeight: 600, wordBreak: 'break-word' }}>{row.value}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={hintSx}>Точка сохранится как местоположение аптеки. Изменить ее можно в любой момент в профиле.</Typography>
          {saveError && <Alert severity="error" sx={alertSx}>{saveError}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" disabled={saving} onClick={() => { setMapStart(view); setStep('adjust'); }} sx={outlinedButtonSx}>Назад</Button>
            <Button variant="contained" disabled={saving} onClick={save} sx={{ ...primaryButtonSx, minWidth: 120 }}>
              {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Сохранить'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GeoWizard;
